import { useStatsigClient } from "@statsig/react-bindings";
import { useRouter } from "next/router";
import Script from "next/script";
import React, { useContext, useEffect, useState } from "react";
import type { ClientInitializeResponse } from "statsig-node-lite/dist/Evaluator";

import { EDO_SLUG } from "@every.org/common/src/entity/constants";
import {
  AdminLevel,
  NonprofitAdminStatus,
} from "@every.org/common/src/entity/types";
import {
  clientRouteMetas,
  ClientRouteName,
} from "@every.org/common/src/helpers/clientRoutes";
import { getUserFullName } from "@every.org/common/src/helpers/username";

import { AuthContext } from "src/context/AuthContext";
import { AuthState, AuthStatus } from "src/context/AuthContext/types";
import { useBloomProfileContext } from "src/context/bloomProfileContext";
import {
  getTestingId,
  useNonprofitExperiment,
  useUserExperiment,
} from "src/utility/abtesting";
import { EMBED_PATH_PREFIX } from "src/utility/embedPage";
import { getWindow } from "src/utility/window";

/**
 * Who's signed in, as a Statsig user (userID, email, custom), which is what
 * the Bloom widget takes. Anyone not signed in, guests included, is {}: the
 * widget starts its chat over when the userID changes, and signing in
 * shouldn't do that.
 */
function getBloomUser(
  { user }: AuthState,
  profileContext: ReturnType<typeof useBloomProfileContext>
) {
  if (!user) {
    return {};
  }
  return {
    userID: user.id,
    email: user.email ?? undefined,
    custom: {
      name: getUserFullName(user) ?? undefined,
      username: user.username,
      // User records can originate as guests; this is not necessarily signup.
      userCreatedAt: user.createdAt?.toISOString(),
      // Like SUPER_ADMIN for staff, who can manage any nonprofit, whatever
      // adminFor says
      adminLevel: user.adminLevelForDisplay,
      // Only the nonprofits they administer directly: the others are a fiscal
      // sponsor's projects, which can run into the hundreds
      adminFor: user.adminFor
        ?.filter(({ directAdmin }) => directAdmin)
        .map(({ nonprofit, status }) => ({
          name: nonprofit.name,
          slug: nonprofit.primarySlug,
          status,
        })),
      ...profileContext,
    },
  };
}

/**
 * The super admin pages (src/pages/SuperAdmin), for every.org staff
 */
const SUPER_ADMIN_PATHS = [
  ClientRouteName.SUPER_ADMIN,
  ClientRouteName.NEW_NONPROFIT,
].map((route) => clientRouteMetas[route].path);

/**
 * The nonprofit admin pages, where the widget shows up by itself
 */
const NONPROFIT_ADMIN_PAGE =
  "/[nonprofitSlugOrUsername]/admin/[[...adminPath]]";

/**
 * The My Giving pages (src/pages/my-giving), for donors
 */
const MY_GIVING_PATH = clientRouteMetas[ClientRouteName.MY_GIVING].path;
const isMyGivingPage = (pathname: string) =>
  pathname === MY_GIVING_PATH || pathname.startsWith(`${MY_GIVING_PATH}/`);

/**
 * The widget's script, and Every.org's key for Bloom, which isn't secret:
 * like Statsig's client key, it's in the page either way
 */
const BLOOM_WIDGET_URL = "https://js.bloomresearch.ai/widget.js";
const BLOOM_SITE_KEY = "bloom_98383321aaaa4b70bad035e74efbaa34";

/**
 * Whether the widget loads for this visitor. In production, the Statsig
 * experiment bloom_widget decides, by nonprofit, for its confirmed admins, on
 * its admin pages: there, the widget loads when the nonprofit's group has
 * `show` on, and the nonprofit counts as exposed. Anyone else, like someone
 * who has only asked to be an admin, gets neither. Super admins, every.org
 * staff who can manage any nonprofit, get whatever Statsig gives them on any
 * nonprofit's admin pages, like an override, without counting. The widget doesn't show up by itself on other pages, so they
 * neither load it nor count, but once loaded, it stays until a full page load,
 * so a conversation can carry on as the visitor moves around the site.
 * Everywhere else, like staging, it loads for everyone.
 */
function useBloomExperiment(
  authState: AuthState,
  pathname: string,
  pageNonprofitId: string | undefined
) {
  const { client } = useStatsigClient();
  const production = process.env.NEXT_PUBLIC_ENVIRONMENT_NAME === "production";
  // Statsig gets a page's nonprofit only after the page renders (see
  // StatsigProvider), and then loads its values, so wait for both, rather than
  // use the last nonprofit's group or log an exposure without one
  const statsigHasPage =
    production &&
    pathname === NONPROFIT_ADMIN_PAGE &&
    !!pageNonprofitId &&
    client.loadingStatus === "Ready" &&
    client.getContext().user.customIDs?.nonprofitId === pageNonprofitId;
  const superAdmin =
    authState.user?.adminLevelForDisplay === AdminLevel.SUPER_ADMIN;
  const confirmedAdmin = !!authState.user?.adminFor?.some(
    ({ nonprofit, status }) =>
      nonprofit.id === pageNonprofitId &&
      status === NonprofitAdminStatus.CONFIRMED
  );
  const counted = statsigHasPage && !superAdmin && confirmedAdmin;
  const experiment = useNonprofitExperiment(
    "bloom_widget",
    !counted,
    pageNonprofitId
  );
  if (!production) {
    return true;
  }
  if (counted) {
    return experiment.get("show", false);
  }
  // useNonprofitExperiment gives the default value whenever it doesn't log an
  // exposure, so super admins read the experiment straight from Statsig
  return (
    statsigHasPage &&
    superAdmin &&
    client
      .getExperiment("bloom_widget", { disableExposureLog: true })
      .get("show", false)
  );
}

/**
 * Whether the widget loads for this visitor on the My Giving pages. In
 * production, the Statsig experiment bloom_my_giving decides, by user, for
 * signed-in donors: there, the widget loads when their group has `show` on,
 * and they count as exposed. Confirmed admins of any nonprofit can be in
 * bloom_widget, so they're kept out of this one, neither loading it nor
 * counting, so neither experiment's results mix in the other's widget. Super
 * admins and confirmed admins of Every.org itself always get the widget here,
 * without counting. Like bloom_widget, once it's loaded it stays as the
 * visitor moves to other pages, until a full page load.
 */
function useBloomMyGivingExperiment(authState: AuthState, pathname: string) {
  const { client } = useStatsigClient();
  const production = process.env.NEXT_PUBLIC_ENVIRONMENT_NAME === "production";
  const { user } = authState;
  const onMyGiving = production && !!user && isMyGivingPage(pathname);
  const superAdmin = user?.adminLevelForDisplay === AdminLevel.SUPER_ADMIN;
  const confirmedAdminFor = (user?.adminFor ?? []).filter(
    ({ status }) => status === NonprofitAdminStatus.CONFIRMED
  );
  const everyOrgAdmin = confirmedAdminFor.some(
    ({ nonprofit }) => nonprofit.primarySlug === EDO_SLUG
  );
  // Statsig gets who's signed in only after auth loads (see StatsigProvider),
  // and then loads their values, so wait for both, rather than log an
  // exposure for the ID they had before signing in
  const statsigHasUser =
    client.loadingStatus === "Ready" &&
    client.getContext().user.userID ===
      getTestingId({ authState, createIdIfMissing: false });
  const counted =
    onMyGiving &&
    statsigHasUser &&
    !superAdmin &&
    !everyOrgAdmin &&
    confirmedAdminFor.length === 0;
  const experiment = useUserExperiment("bloom_my_giving", !counted);
  if (!onMyGiving) {
    return false;
  }
  if (superAdmin || everyOrgAdmin) {
    return true;
  }
  return counted && experiment.get("show", false);
}

type BloomProviderProps = {
  children: React.ReactNode;
  statsigInitProps?: Record<string, unknown>;
};

/**
 * Loads the Bloom widget (js.bloomresearch.ai): an assistant in the corner of
 * the page that does things on the site for visitors when they ask. Loads
 * everywhere but the end-to-end tests, except in the embedded donate flow,
 * which partner sites show in an iframe, and on the super admin pages.
 * In production, it loads only for the visitors the bloom_widget and
 * bloom_my_giving experiments pick (see useBloomExperiment and
 * useBloomMyGivingExperiment).
 */
export const BloomProvider = ({
  children,
  statsigInitProps,
}: BloomProviderProps) => {
  const authState = useContext(AuthContext);
  const router = useRouter();
  const { pathname, asPath } = router;
  const profileContext = useBloomProfileContext(router);
  // Compare only allowlisted facts, not unrelated store updates.
  const profileContextKey = JSON.stringify(profileContext);
  const [loaded, setLoaded] = useState(false);
  // The page's nonprofit, as StatsigProvider gets it
  const nonprofitId = (statsigInitProps as ClientInitializeResponse)?.user
    ?.customIDs?.nonprofitId;
  const loadsForNonprofitAdmins = useBloomExperiment(
    authState,
    pathname,
    nonprofitId
  );
  const loadsForDonors = useBloomMyGivingExperiment(authState, pathname);
  const loadsWidget = loadsForNonprofitAdmins || loadsForDonors;

  // Like StatsigProvider's updateUserAsync: tell the widget who's signed in
  // once auth has loaded, and again on navigation or saved profile changes.
  // Always assemble the entire custom object; updateUser need not deep-merge.
  useEffect(() => {
    if (loaded && authState.status !== AuthStatus.LOADING) {
      // Identity is ready independently of nonprofit data. Store updates send
      // another complete snapshot as the selected nonprofit's facts arrive.
      getWindow()?.Bloom?.updateUser(getBloomUser(authState, profileContext));
    }
    // profileContextKey represents all profileContext values.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, authState, asPath, pathname, profileContextKey]);

  const environment = process.env.NEXT_PUBLIC_ENVIRONMENT_NAME;
  return (
    <React.Fragment>
      {children}
      {environment !== "test" &&
        loadsWidget &&
        !pathname.startsWith(EMBED_PATH_PREFIX) &&
        !SUPER_ADMIN_PATHS.includes(pathname) && (
          // The key tells Bloom it's Every.org at any origin, so a Vercel
          // preview goes in this build's environment, like staging, instead
          // of being a new site. www.every.org and staging.every.org keep
          // the environments Bloom has for them
          <Script
            src={BLOOM_WIDGET_URL}
            data-key={BLOOM_SITE_KEY}
            data-environment={environment}
            onLoad={() => setLoaded(true)}
          />
        )}
    </React.Fragment>
  );
};
